import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faShoppingCart, 
  faStar, 
  faStarHalfAlt, 
  faHeart,
  faEye,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { Product } from '../../utils/types';
import { theme } from '../../styles/theme';
import { Button } from '../../styles/components';
import { useCart } from '../../context/CartContext';

interface ProductCardProps {
  product: Product;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

const Card = styled.div`
  border-radius: ${theme.borderRadius.large};
  overflow: hidden;
  background-color: ${theme.colors.background};
  box-shadow: ${theme.shadows.small};
  transition: ${theme.transitions.medium};
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    border-radius: ${theme.borderRadius.medium};
  }
  
  &:hover {
    box-shadow: ${theme.shadows.medium};
    transform: translateY(-5px);
    
    .product-overlay {
      opacity: 1;
    }
    
    .product-image img {
      transform: scale(1.05);
    }
    
    .quick-actions {
      opacity: 1;
      transform: translateY(0);
    }
    
    .product-title {
      color: ${theme.colors.primary};
    }
  }
`;

const ImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  padding-top: 100%; /* 1:1 Aspect Ratio */
`;

const ProductImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s ease;
`;

const ProductOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, 
    rgba(0, 0, 0, 0) 60%, 
    rgba(0, 0, 0, 0.4) 100%
  );
  opacity: 0;
  transition: ${theme.transitions.medium};
  class-name: product-overlay;
`;

const Discount = styled.span`
  position: absolute;
  top: ${theme.spacing.sm};
  left: ${theme.spacing.sm};
  background: ${theme.colors.gradient.gold};
  color: ${theme.colors.secondary};
  font-weight: bold;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.pill};
  font-size: 0.75rem;
  z-index: 1;
  box-shadow: ${theme.shadows.small};
`;

const OutOfStock = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.6);
  color: ${theme.colors.background};
  font-weight: 600;
  font-size: 1rem;
  z-index: 2;
  text-transform: uppercase;
  letter-spacing: 1px;
  backdrop-filter: blur(2px);
`;

const QuickActions = styled.div`
  position: absolute;
  bottom: ${theme.spacing.md};
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: ${theme.spacing.sm};
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.3s ease;
  z-index: 3;
  class-name: quick-actions;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    opacity: 1;
    transform: translateY(0);
    bottom: ${theme.spacing.sm};
  }
`;

const ActionButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: ${theme.colors.background};
  color: ${theme.colors.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: ${theme.transitions.short};
  box-shadow: ${theme.shadows.medium};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 32px;
    height: 32px;
    font-size: 0.8rem;
  }
  
  &:hover {
    background-color: ${theme.colors.primary};
    color: ${theme.colors.secondary};
    transform: translateY(-3px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &.wishlist-active {
    background-color: ${theme.colors.primary};
    color: ${theme.colors.secondary};
  }
  
  &.in-cart {
    background-color: #28A745;
    color: white;
  }
`;

const CardContent = styled.div`
  padding: ${theme.spacing.md};
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm};
  }
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 10%;
    right: 10%;
    height: 1px;
    background: linear-gradient(to right, 
      transparent, 
      rgba(0, 0, 0, 0.09), 
      transparent
    );
  }
`;

const Category = styled.span`
  color: ${theme.colors.textLight};
  font-size: 0.75rem;
  margin-bottom: ${theme.spacing.xs};
  display: block;
  text-transform: capitalize;
  letter-spacing: 0.5px;
`;

const ProductTitle = styled(Link)`
  font-weight: 500;
  color: ${theme.colors.secondary};
  text-decoration: none;
  margin-bottom: ${theme.spacing.sm};
  display: block;
  font-size: 1rem;
  transition: ${theme.transitions.short};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  class-name: product-title;
  
  &:hover {
    color: ${theme.colors.primary};
  }
`;

const Price = styled.div`
  font-weight: 600;
  font-size: 1.125rem;
  margin-top: auto;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  
  .original-price {
    text-decoration: line-through;
    color: ${theme.colors.gray};
    font-weight: 400;
    font-size: 0.85rem;
  }
  
  .discounted-price {
    color: ${theme.colors.secondary};
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 100%;
      height: 2px;
      background: ${theme.colors.gradient.gold};
      opacity: 0.6;
    }
  }
`;

const Rating = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${theme.spacing.sm};
  
  svg {
    color: ${theme.colors.primary};
    margin-right: 1px;
    font-size: 0.8rem;
  }
  
  .rating-text {
    margin-left: ${theme.spacing.xs};
    font-size: 0.75rem;
    color: ${theme.colors.textLight};
  }
`;

const AddedToCartMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(40, 167, 69, 0.9);
  color: white;
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.pill};
  font-weight: 500;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  box-shadow: ${theme.shadows.medium};
  animation: ${fadeIn} 0.3s forwards, fadeOut 0.3s forwards 2s;
  
  @keyframes fadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
`;

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { addItem, items } = useCart();
  const [inWishlist, setInWishlist] = useState(false);
  const [showAddedMessage, setShowAddedMessage] = useState(false);
  
  const isInCart = items.some(item => item.id === product.id);
  
  const handleAddToCart = () => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.discountPrice || product.price,
      image: product.images[0],
      quantity: 1,
      category: product.category,
    });
    
    setShowAddedMessage(true);
    setTimeout(() => {
      setShowAddedMessage(false);
    }, 2500);
  };
  
  const handleToggleWishlist = () => {
    setInWishlist(!inWishlist);
    // In a real app, you'd call a function to add/remove from wishlist
  };
  
  // Function to render stars based on rating
  const renderRatingStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    // Add full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(<FontAwesomeIcon key={`full-${i}`} icon={faStar} />);
    }
    
    // Add half star if needed
    if (hasHalfStar) {
      stars.push(<FontAwesomeIcon key="half" icon={faStarHalfAlt} />);
    }
    
    return stars;
  };
  
  // Calculate discount percentage
  const calculateDiscount = () => {
    if (!product.discountPrice) return null;
    
    const discount = ((product.price - product.discountPrice) / product.price) * 100;
    return Math.round(discount);
  };
  
  const discount = calculateDiscount();
  
  return (
    <Card>
      {showAddedMessage && (
        <AddedToCartMessage>
          <FontAwesomeIcon icon={faCheck} /> Added to cart
        </AddedToCartMessage>
      )}
      
      <ImageContainer className="product-image">
        <ProductImage src={product.images[0]} alt={product.name} />
        <ProductOverlay />
        
        {discount && <Discount>-{discount}%</Discount>}
        {!product.inStock && <OutOfStock>Out of Stock</OutOfStock>}
        
        <QuickActions>
          <ActionButton 
            onClick={handleToggleWishlist}
            className={inWishlist ? 'wishlist-active' : ''}
            aria-label="Add to wishlist"
            title="Add to wishlist"
          >
            <FontAwesomeIcon icon={faHeart} />
          </ActionButton>
          
          <ActionButton
            onClick={handleAddToCart}
            disabled={!product.inStock || isInCart}
            className={isInCart ? 'in-cart' : ''}
            aria-label={isInCart ? 'In cart' : 'Add to cart'}
            title={isInCart ? 'In cart' : 'Add to cart'}
          >
            <FontAwesomeIcon icon={isInCart ? faCheck : faShoppingCart} />
          </ActionButton>
          
          <ActionButton
            as={Link}
            to={`/product/${product.id}`}
            aria-label="View details"
            title="View details"
          >
            <FontAwesomeIcon icon={faEye} />
          </ActionButton>
        </QuickActions>
      </ImageContainer>
      
      <CardContent>
        <Category>{product.category.replace('-', ' ')}</Category>
        <ProductTitle to={`/product/${product.id}`}>{product.name}</ProductTitle>
        
        {product.rating && (
          <Rating>
            {renderRatingStars(product.rating)}
            <span className="rating-text">{product.rating.toFixed(1)}</span>
          </Rating>
        )}
        
        <Price>
          {product.discountPrice ? (
            <>
              <span className="discounted-price">${product.discountPrice.toFixed(2)}</span>
              <span className="original-price">${product.price.toFixed(2)}</span>
            </>
          ) : (
            <span>${product.price.toFixed(2)}</span>
          )}
        </Price>
      </CardContent>
    </Card>
  );
};

export default ProductCard; 